<template>
  <div
    class="prevent-select"
    @contextmenu.stop.prevent="contextMenuClick"
    @click.ctrl.prevent="toggleItemSelection(false)"
    @click.shift.prevent="toggleItemSelection(true)"

    :style="{ opacity: isDragOver ? 0.5 : 1 }"
    @dragover.stop.prevent="handleDomDragOver"
    @dragenter.stop.prevent="onDragEnter"
    @dragleave.stop.prevent="onDragLeave"
    @drop.prevent="handleDomDrop"

    :draggable="isDraggableItem(item, asSearchResult)"
    @dragstart="onDragStart($event, item)"
    @dragend="onDragEnd($event, item)"

    role="listitem"
  >
    <!-- Select box -->
    <div data-cy="list-nodeItem-selectColumn">
      <div>
        <NodeItemSelect
          v-if="!asSearchResult"
          @toggleItemSelection="toggleItemSelection"
          :node="item"
          data-cy="list-nodeItem-selectBox"
        />
      </div>
    </div>

    <!-- Folder icon -->
    <div class="no-underline-on-hover" style="justify-content: center;">
      <router-link
        :to="getNodeLink"
        class="no-underline-on-hover"
        :tabindex="-1"
        :aria-label="isArchived || isDeleted ? undefined : item.name"
      >
        <div v-if="getClientLogo && !isDeleted" v-bind:style="getClientLogo"></div>
        <v-icon v-if="!getClientLogo || isDeleted" size="86px" class="no-underline-on-hover">
          {{ getNodeIconName }}
        </v-icon>
      </router-link>
    </div>

    <!-- Node Name -->
    <div data-cy="list-nodeItem-nameColumn">
      <div style="max-width: 100%;">
        <v-tooltip location="bottom" open-delay="200" :aria-label="item.name">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="text-truncate"
              style="width: 100%"
              data-cy="list-nodeItem-name"
            >
              <router-link
                :to="getNodeLink"
                class="text-truncate"
                :class="getClass()"
                :aria-label="$t('routes.node.a11y.enterFolder', { name: item.name })"
              >
                {{ item.name }}
              </router-link>
            </div>
          </template>
          <span>{{ item.name }}</span>
        </v-tooltip>

        <div data-cy="list-nodeItem-content">
          <span v-if="countChildren > 0">{{ $t('routes.node.totalNodes', countChildren) }}</span>
          <span v-if="countChildrenArchived > 0">
            ({{ $t('routes.node.archivedNodes', countChildrenArchived) }})
          </span>
          <span v-if="countChildren > 0 && countPods > 0"> & </span>
          <span v-if="countPods > 0">{{ $t('routes.node.totalPods', countPods) }}</span>
          <span v-if="countPodsArchived > 0">
            ({{ $t('routes.node.archivedPods', countPodsArchived) }})
          </span>
        </div>
        <div data-cy="list-nodeItem-projectStatus">
          {{ getProjectStatus }}
        </div>
        <v-chip
          v-if="item.teamonly === accessType.TEAM_ONLY"
          data-cy="list-nodeItem-accessType-teamOnly"
          size="x-small"
          class="mr-1"
        >
          {{ $t('routes.node.accessTypeTeamOnly') }}
        </v-chip>
        <v-chip v-if="item.archived === archiveStatus.ARCHIVED" size="x-small" data-cy="list-podItem-archiveStatus-archived">
          {{ $t('routes.node.statusArchived') }}
        </v-chip>
        <v-chip v-if="item.archived === archiveStatus.PENDING" size="x-small" data-cy="list-podItem-archiveStatus-pending">
          {{ $t('routes.node.statusPendingUnarchive') }}
        </v-chip>
        <v-chip v-if="item.archived === archiveStatus.ARCHIVING" size="x-small" data-cy="list-podItem-archiveStatus-archiving">
          {{ $t('routes.node.statusArchiving') }}
        </v-chip>
        <v-chip v-if="item.archived === archiveStatus.UNARCHIVING" size="x-small" data-cy="list-podItem-archiveStatus-unarchiving">
          {{ $t('routes.node.statusUnarchiving') }}
        </v-chip>
        <div v-if="item.retentionperiod > 0" size="small" class="pt-1">
          <v-icon size="small" icon="mdi-delete-clock" class="mr-1"></v-icon>
          {{ retentionPeriod }}
        </div>
      </div>

    </div>

    <!-- Node size -->
    <div />

    <!-- Created by -->
    <div>
      <div v-if="isDeleted">
        <small
          v-if="item.deletedat"
          data-cy="list-podItem-date"
          :aria-label="$t('routes.node.a11y.deletedAt', { date: isoToDatetime(item.deletedat) })"
        >
          {{ isoToDatetime(item.deletedat) }}
        </small>
      </div>
      <div v-else>
        <small :aria-label="$t('routes.node.a11y.createdAt', { date: isoToDatetime(item.createdat) })">
          {{ isoToDatetime(item.createdat) }}
        </small>
        <br/>
        <v-tooltip location="bottom" open-delay="200" :aria-label="this.getCreatedBy">
          <template v-slot:activator="{ props }">
            <span
              v-bind="props"
              :aria-label="$t('routes.node.a11y.createdBy', { name: getCreatedBy })"
            >
              {{ getCreatedBy }}
            </span>
          </template>
          <span>{{ getCreatedBy }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Notes -->
    <div>
      <NodeItemNote
        :node="item"
        :note="item.note"
        data-cy="list-nodeItem-noteArea"
      />
    </div>

    <!-- Actions -->
    <div data-cy="list-nodeItem-actionsColumn">
      <v-btn
        @click="contextMenuBtnClick"
        icon
        variant="flat"
        width="24"
        height="24"
        rounded="circle"
        color="transparent"
        :aria-label="$t('routes.node.a11y.openActionsMenu', { name: item.name })"
      >
        <v-icon data-cy="list-podItem-actions-btn">mdi-dots-vertical</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import NodeItemNote from "./NodeItemNote.vue"
import NodeItemSelect from "./NodeItemSelect.vue"
import { formatSize, isoToDatetime } from '@/components/formatters.js';

import MovableItem from '@/components/mixin/MovableItem.js';
import MovableItemDropzone from '@/components/mixin/MovableItemDropzone.js';

export default {
  name: "ListNodeItem",
  mixins: [MovableItem, MovableItemDropzone],
  emits: ['toggleItemSelection', 'showContextMenu', 'droppedMovableItems'],
  components: {
    NodeItemNote,
    NodeItemSelect,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    isDeleted: {
      type: Boolean,
      default: false,
      required: false,
    },
    isArchived: {
      type: Boolean,
      default: false,
      required: false,
    },
    disablePodRequests: {
      type: Boolean,
      default: false,
      required: false,
    }
  },

  data: function() {
    return {
      fullNode: null,
      pods: [],
    }
  },

  mounted: function() {
  },

  watch: {
    disablePodRequests: function(newValue) {
      if (!newValue) {
        this.refreshSubPods();
      }
    }
  },

  methods: {
    isoToDatetime,
    formatSize,
    async refreshSubPods() {
      try {
        const podStore = usePodStore();
        this.pods = await podStore.fetchPodsByNodeId(this.item.id);
      } catch (error) {
        console.log("Error while refreshing pods... " + error.toString())
      }
    },
    toggleItemSelection: function(holdingShift) {
      const payload = {
        holdingShift: holdingShift,
        item: this.item
      };

      this.$emit("toggleItemSelection", payload);
    },
    contextMenuClick(event) {
      // if (this.asSearchResult || this.item.archived !== 0) {
      //   return;
      // }
      // if (this.item.archived !== 0) {
      //   return;
      // }

      const eventData = {
        contextMenuNode: this.item,
        contextMenuX: event.clientX,
        contextMenuY: event.clientY,
      };

      this.$emit('showContextMenu', eventData);
    },
    contextMenuBtnClick(event) {
      // if (this.asSearchResult || this.item.archived !== 0) {
      //   return;
      // }
      // if (this.item.archived !== 0) {
      //   return;
      // }

      const eventData = {
        contextMenuNode: this.item,
        contextMenuX: event.clientX - event.offsetX + event.target.offsetWidth,
        contextMenuY: event.clientY - event.offsetY + event.target.offsetHeight,
      };

      this.$emit('showContextMenu', eventData);
    },
    getClass: function() {
      return {
        'red--text': this.item.deletedat !== null || (
          this.isDeleted && (
            this.item.type === this.nodeTypes.CLIENT ||
            this.item.type === this.nodeTypes.TENANT
          )
        ),
      };
    },
  },

  computed: {
    childNodes: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getChildNodes;
    },
    nodeTypes: function() {
      const commonStore = useCommonStore();
      return commonStore.nodeTypes;
    },
    projectStatuses: function() {
      const commonStore = useCommonStore();
      return commonStore.projectStatuses;
    },
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    accessType() {
      const commonStore = useCommonStore();
      return commonStore.accessType;
    },
    countChildren: function() {
      if (typeof this?.item?.subfoldersCount === 'number') {
        return this.item.subfoldersCount;
      } else if (this.item && this.item.children && this.item.children.length) {
        return this.item.children.length;
      } else if (this.fullNode && this.fullNode.children) {
        return this.fullNode.children.length
      }

      return 0;
    },
    countChildrenArchived: function() {
      if (typeof this?.item?.archivedSubfoldersCount === 'number') {
        return this.item.archivedSubfoldersCount;
      } else if (this.item && this.item.children && this.item.children.length) {
        const archived = this.item.children.filter((c) => c.archived === this.archiveStatus.ARCHIVED);
        return archived.length;
      } else if (this.fullNode && this.fullNode.children) {
        const archived = this.fullNode.children.filter((c) => c.archived === this.archiveStatus.ARCHIVED);
        return archived.length;
      }

      return 0;
    },
    countPods: function() {
      return this.item?.filesCount || 0;
    },
    countPodsArchived: function() {
      if (this.pods && this.pods.length > 0) {
        const archivedPods = this.pods.filter((x) => x.archived === this.archiveStatus.ARCHIVED) || [];
        return archivedPods.length;
      }

      return 0;
    },
    getUpdatedBy: function() {
      // if (!this.fullNode) {
      //   return "";
      // }
      // return this.fullNode.updatedby ? this.fullNode.updatedby.name : "";
      return this.item.updatedby ? `${this.item.updatedby.firstname} ${this.item.updatedby.lastname}` : this.$t('common.deletedUser');
    },
    getCreatedBy: function() {
      // if (!this.fullNode) {
      //   return "";
      // }
      // return this.fullNode.createdby ? this.fullNode.createdby.name : "";
      return this.item.createdby ? `${this.item.createdby.firstname} ${this.item.createdby.lastname}` : this.$t('common.deletedUser');
    },
    getNodeIconName: function() {
      switch (this.item.type) {
        case this.nodeTypes.PROJECT: return "mdi-briefcase-clock";
        case this.nodeTypes.CLIENT: return "tw tw-office-building";
        case this.nodeTypes.TENANT: return "mdi-home-assistant";
        default: return "mdi-folder";
      }
    },
    getProjectStatus: function() {
      if (this.item.type !== this.nodeTypes.PROJECT) {
        return '';
      }

      switch (this.item.projectstatus) {
        case this.projectStatuses.LOGGING: return this.$t('menus.createNode.projectStatusLogging');
        case this.projectStatuses.EDITING: return this.$t('menus.createNode.projectStatusEditing');
        case this.projectStatuses.REVIEWING: return this.$t('menus.createNode.projectStatusReviewing');
        case this.projectStatuses.DELIVERED: return this.$t('menus.createNode.projectStatusDelivered');
        default: return '';
      }
    },
    getNodeLink: function() {
      if (this.isDeleted) {
        return { path: `/recycle-bin/${this.item.id}` };
      }

      return {path: '/node/' + this.item.id};
    },
    getClientLogo: function() {
      if (this.item.clientlogourl) {
        return {
          width: '140px',
          height: '80px',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundImage: 'url("'+this.item.clientlogourl+'")'
        };
      }

      return false;
    },
    retentionPeriod: function () {
      if (this.item.retentionperiod > 0) {
        const retentionType = this.item.retentiontype.toLowerCase();
        return this.$t('routes.node.retentionPeriod') + ' ' + this.$t(`common.time.${retentionType}`, this.item.retentionperiod);
      }

      return this.$t('routes.node.noRetention');
    },
  }
};
</script>

<style scoped>
.no-underline-on-hover {
  text-decoration: none; /* Removes underline in general */
}

.no-underline-on-hover:hover,
.no-underline-on-hover:active,
.no-underline-on-hover:target,
.no-underline-on-hover:focus {
  text-decoration: none !important; /* Ensures no underline on hover */
}
</style>
